import {Component, EventEmitter, Input, OnChanges, OnInit, AfterViewInit, OnDestroy, Output, ViewChild, SimpleChanges, ElementRef, NgZone} from '@angular/core';
import {Draft} from "../../../model/Draft";
import {Book} from "../../../model/Book";
import {TRIM_SIZE_PRESETS, TrimSizePreset} from "../projects/draft/project-draft.component";
import {ToolbarPreferencesService} from "../../service-ui/toolbar-preferences.service";
import {MediaMatcher} from "@angular/cdk/layout";
import {ResourceService} from "../../service-data/resource.service";
import {LanguagePipe} from "../../pipe/language.pipe";
import {MeasurementUnits} from "../../service-ui/measurement-units";

@Component({
  selector: 'app-resource',
  templateUrl: './resource.component.html',
  styleUrls: ['./resource.component.css']
})

export class ResourceComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  // Persist zoom across component destroy/recreate (e.g. switching interior ↔ cover)
  // Baseline: 0.25 = 100% zoom display (with 400x multiplier), matching production baseline
  // This corresponds to 288 DPI at natural pixel size
  private static persistedZoomLevel: number = 0.25;

  @ViewChild('image', { static: true }) image;
  @ViewChild('coverPage') coverWrapper;
  @ViewChild('coverClippingRect') coverClipping;
  @ViewChild('coverBounds') coverMarkers;
  @ViewChild('coverLines') coverLines;
  @ViewChild('backCoverBounds') back;
  @ViewChild('spineCoverBounds') spine;
  @ViewChild('frontCoverBounds') front;
  @ViewChild('backCoverLine') backLine;
  @ViewChild('spineCoverLine') spineLine;
  @ViewChild('frontCoverLine') frontLine;

  @Input() draft: Draft;
  @Input() book: Book;
  @Input() resourceId: number;
  @Input() verticalShift: number;
  @Input() horizontalShift: number;
  @Input() scale: number;
  @Input() pageNumber: number;
  @Input() dpi: number;
  @Input() orgId: number;
  @Input() bodyOrCover: string;
  @Input() bindingType: string;

  @Output() shiftChanged = new EventEmitter<{verticalShift: number, horizontalShift: number}>();
  @Output() scaleChanged = new EventEmitter<number>();
  @Output() trimChanged = new EventEmitter<{width: number, height: number}>();

  // Display scale converts points (72/inch) to pixels at current zoom
  // At zoom=1.0, displayScale=4.0 for 288 DPI (288/72=4), matching interior
  // This is set in ngOnInit and onZoomChange as: displayScale = zoomLevel * 4.0
  displayScale: number = 4.0;
  bleed: number = 15;
  trimWobble: number = 0.25 * 72; // 1/4 inch in points

  toolbarPositions = ['bottom-left', 'top-left', 'top-right', 'bottom-right'];
  currentToolbarPosition: string = 'bottom-left';
  coverDragPosition: { x: number, y: number } = { x: 100, y: 400 };

  // Toolbar expansion for mobile
  toolbarExpanded: boolean = false;

  // Trim size
  trimSizePresets: TrimSizePreset[] = TRIM_SIZE_PRESETS;
  selectedTrimSize: string = '';
  customWidth: number = 0;
  customHeight: number = 0;

  // Custom trim edit mode
  editingCustomTrim: boolean = false;
  editCustomWidth: number = 0;
  editCustomHeight: number = 0;

  // Units
  unit: 'in' | 'mm' = 'in';
  unitMultiplier: number = 72; // points per inch

  // Zoom
  zoomLevel: number = 1.0;

  private viewReady = false;
  private cachedImageElement: HTMLImageElement = null;
  coverImageLoaded: boolean = false;

  /**
   * True when a PDF resource has actually been uploaded for this side of the
   * Draft/Book. When false the view shows a "No PDF Uploaded Yet" message
   * instead of an indefinite "Loading Image" placeholder.
   */
  get hasResource(): boolean {
    return !!this.resourceId;
  }

  Math = Math;

  constructor (private toolbarPrefs: ToolbarPreferencesService, private media: MediaMatcher, private resourceService: ResourceService) {
  }

  ngOnInit() {
    // Determine default expanded state based on screen width
    const isNarrowScreen = this.media.matchMedia('(max-width: 900px)').matches;
    const defaultExpanded = !isNarrowScreen;

    // Load saved preferences or use default
    this.toolbarExpanded = this.toolbarPrefs.getCoverToolbarExpanded(defaultExpanded);
    this.currentToolbarPosition = this.toolbarPrefs.getCoverToolbarPosition('bottom-left');
    this.coverDragPosition = this.toolbarPrefs.getCoverToolbarXY(null);
    if (!this.coverDragPosition) {
      // Default: 10% from left, 60% from top of viewport
      this.coverDragPosition = { x: Math.round(window.innerWidth * 0.1), y: Math.round(window.innerHeight * 0.6) };
    }

    this.initTrimSize();
    // Restore persisted zoom level from previous instance (survives interior ↔ cover switch)
    this.zoomLevel = ResourceComponent.persistedZoomLevel;
    // Convert zoom to displayScale: zoom 1.0 = 288 DPI (4.0 display scale)
    this.displayScale = this.zoomLevel * 4.0;
  }

  ngAfterViewInit() {
    this.viewReady = true;
    // Use setTimeout to let the DOM settle after *ngIf re-creation
    setTimeout(() => {
      this.refreshImagePosition();
    }, 0);
  }

  ngOnDestroy() {
    // Clean up drag listeners
    if (this.boundDragMove) {
      document.removeEventListener('mousemove', this.boundDragMove);
      document.removeEventListener('touchmove', this.boundDragMove);
    }
    if (this.boundDragEnd) {
      document.removeEventListener('mouseup', this.boundDragEnd);
      document.removeEventListener('touchend', this.boundDragEnd);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.viewReady) { return; }
    // Defer to next tick to ensure DOM is settled
    setTimeout(() => {
      this.refreshImagePosition();
    }, 0);
  }

  /** Get body dimensions from either draft or book */
  private getBodyDimensions(): { height: number, width: number, spine: number } {
    if (this.draft && this.draft.draftBody) {
      return {
        height: this.draft.draftBody.height,
        width: this.draft.draftBody.width,
        spine: this.draft.draftBody.spine
      };
    }
    if (this.book && this.book.body) {
      return {
        height: this.book.body.height,
        width: this.book.body.width,
        spine: this.book.body.spine || 0
      };
    }
    return { height: 648, width: 432, spine: 0 };
  }

  refreshImagePositionDetails(cachedImage: any) {
    try {
      if (!this.image || !this.image.nativeElement) {
        console.error('ResourceComponent: image ViewChild is undefined');
        return;
      }

      this.image.nativeElement.style.display = 'block';
      this.image.nativeElement.src = cachedImage.src;

      const naturalWidth = cachedImage.naturalWidth || cachedImage.width;
      const naturalHeight = cachedImage.naturalHeight || cachedImage.height;

      if (!naturalWidth || !naturalHeight) {
        return; // Image not loaded yet
      }

      // Compute the cover page dimensions from body dimensions + bleed
      const dims = this.getBodyDimensions();
      const borderWidth = this.trimWobble;
      const clipWidth = (dims.width * 2 + dims.spine + borderWidth) * this.displayScale + 2 * this.bleed;
      const clipHeight = (dims.height + borderWidth) * this.displayScale + 2 * this.bleed;

      // Dynamically size the coverPage wrapper to match the zoom level
      if (this.coverWrapper && this.coverWrapper.nativeElement) {
        this.coverWrapper.nativeElement.style.width = clipWidth + 'px';
        this.coverWrapper.nativeElement.style.height = clipHeight + 'px';
      }

      // Size the image based on its physical dimensions (points) scaled to screen pixels.
      // The image is rendered at `this.dpi` pixels per inch; one inch = 72 points.
      // Screen size = (naturalPixels / dpi) * 72 points/inch * displayScale * documentScale
      const ppi = this.dpi || 288;
      const pageScreenWidth = (naturalWidth / ppi) * 72 * this.displayScale * this.scale;
      const pageScreenHeight = (naturalHeight / ppi) * 72 * this.displayScale * this.scale;

      this.image.nativeElement.style.width = pageScreenWidth + 'px';
      this.image.nativeElement.style.height = pageScreenHeight + 'px';
      this.image.nativeElement.style.left = ((clipWidth - pageScreenWidth) / 2 + this.horizontalShift * this.displayScale) + 'px';
      this.image.nativeElement.style.top = ((clipHeight - pageScreenHeight) / 2 + this.verticalShift * this.displayScale) + 'px';

      this.coverImageLoaded = true;
      this.refresh();
    } catch (error) {
      console.error('Error refreshing image position details:', error);
      return;
    }
  }

  refreshImagePosition() {
    if (!this.viewReady) { return; }
    if (!this.resourceId) {
      this.coverImageLoaded = false;
      if (this.image && this.image.nativeElement) {
        this.image.nativeElement.src = '';
      }
      return;
    }

    const imageId = 'resource-' + this.resourceId + '-' + this.pageNumber + '-' + this.dpi;

    let cachedImage: any = document.getElementById(imageId);
    if (!cachedImage) {
      const hiddenImagesElement: any = document.getElementById('hiddenImages');
      if (hiddenImagesElement) {
        const imageElement = document.createElement('img');
        imageElement.id = imageId;
        hiddenImagesElement.appendChild(imageElement);
        cachedImage = imageElement;

        this.resourceService.getImageURL(this.resourceId, 'COLOR', this.dpi, this.pageNumber).subscribe(imageURL => {
          if (imageURL) {
            imageElement.src = imageURL;
            this.cachedImageElement = imageElement;
            if (imageElement.complete && imageElement.naturalWidth > 0) {
              this.refreshImagePositionDetails(imageElement);
            } else {
              const onLoad = () => {
                imageElement.removeEventListener('load', onLoad);
                this.refreshImagePositionDetails(imageElement);
              };
              imageElement.addEventListener('load', onLoad);
              imageElement.addEventListener('error', _ => {
                console.error(`Error loading image ${imageId}`);
              });
            }
          } else {
            console.error(`Failed to get image URL for resource ${this.resourceId}`);
          }
        });
      }
    } else {
      this.cachedImageElement = cachedImage;
      if (cachedImage.complete && cachedImage.naturalWidth > 0) {
        this.refreshImagePositionDetails(cachedImage);
      } else {
        // Use a one-time listener to avoid stacking
        const onLoad = () => {
          cachedImage.removeEventListener('load', onLoad);
          this.refreshImagePositionDetails(cachedImage);
        };
        cachedImage.addEventListener('load', onLoad);
        cachedImage.addEventListener('error', _ => {
          console.error(`Error loading image ${imageId}`);
        });
      }
    }
  }

  public refresh() {
    if (!this.viewReady) { return; }

    const dims = this.getBodyDimensions();
    const cropHeight = dims.height;
    const cropWidth = dims.width;
    const spineWidth = dims.spine;
    const borderWidth = this.trimWobble;

    // Compute the total cover dimensions at the current displayScale
    const wrapWidth = (cropWidth * 2 + spineWidth + borderWidth) * this.displayScale + 2 * this.bleed;
    const wrapHeight = ((cropHeight + borderWidth) * this.displayScale) + 2 * this.bleed;

    // Size the coverPage wrapper
    if (this.coverWrapper && this.coverWrapper.nativeElement) {
      this.coverWrapper.nativeElement.style.width = wrapWidth + 'px';
      this.coverWrapper.nativeElement.style.height = wrapHeight + 'px';
    }

    if (this.coverMarkers && this.coverMarkers.nativeElement) {
      this.coverMarkers.nativeElement.style.top = this.bleed + 'px';
      this.coverMarkers.nativeElement.style.left = this.bleed + 'px';
      this.coverMarkers.nativeElement.style.height = ((cropHeight + borderWidth) * this.displayScale) + 'px';
      this.coverMarkers.nativeElement.style.width = ((cropWidth * 2 + spineWidth + borderWidth) * this.displayScale) + 'px';
    }

    if (this.coverLines && this.coverLines.nativeElement) {
      this.coverLines.nativeElement.style.top = (this.bleed + borderWidth * this.displayScale / 2) + 'px';
      this.coverLines.nativeElement.style.left = this.bleed + 'px';
      this.coverLines.nativeElement.style.height = ((cropHeight + borderWidth) * this.displayScale) + 'px';
      this.coverLines.nativeElement.style.width = ((cropWidth * 2 + spineWidth + borderWidth) * this.displayScale) + 'px';
    }

    if (this.back && this.back.nativeElement) {
      this.back.nativeElement.style.width = (cropWidth - borderWidth) * this.displayScale + 'px';
      this.back.nativeElement.style.height = (cropHeight - borderWidth) * this.displayScale + 'px';
      this.back.nativeElement.style.border = (borderWidth * this.displayScale) + 'px solid red';
    }

    if (this.backLine && this.backLine.nativeElement) {
      this.backLine.nativeElement.style.width = (cropWidth * this.displayScale - 2) + 'px';
      this.backLine.nativeElement.style.marginLeft = borderWidth * this.displayScale / 2 + 'px';
      this.backLine.nativeElement.style.height = (cropHeight * this.displayScale - 2) + 'px';
    }

    if (this.front && this.front.nativeElement) {
      this.front.nativeElement.style.width = (cropWidth - borderWidth) * this.displayScale + 'px';
      this.front.nativeElement.style.height = (cropHeight - borderWidth) * this.displayScale + 'px';
      this.front.nativeElement.style.border = (borderWidth * this.displayScale) + 'px solid red';
    }

    if (this.frontLine && this.frontLine.nativeElement) {
      this.frontLine.nativeElement.style.width = (cropWidth * this.displayScale - 2) + 'px';
      this.frontLine.nativeElement.style.height = (cropHeight * this.displayScale - 2) + 'px';
      this.frontLine.nativeElement.style.marginRight = borderWidth * this.displayScale / 2 + 'px';
    }

    if (this.spine && this.spine.nativeElement && this.spineLine && this.spineLine.nativeElement) {
      this.spine.nativeElement.style.border = (borderWidth * this.displayScale) + 'px solid red';
      if (spineWidth - borderWidth < 0) {
        this.spine.nativeElement.style.width = '0px';
        this.spineLine.nativeElement.style.width = '0px';
      } else {
        this.spine.nativeElement.style.width = (spineWidth - borderWidth) * this.displayScale + 'px';
        this.spineLine.nativeElement.style.width = (spineWidth * this.displayScale - 2) + 'px';
      }
      this.spine.nativeElement.style.height = (cropHeight - borderWidth) * this.displayScale + 'px';
      this.spineLine.nativeElement.style.height = (cropHeight * this.displayScale - 2) + 'px';
    }
  }

  /** Shift the cover by a delta (in points) */
  changeShift(horizontalDelta: number, verticalDelta: number): void {
    this.horizontalShift = this.horizontalShift + horizontalDelta;
    this.verticalShift = this.verticalShift + verticalDelta;
    this.shiftChanged.emit({ verticalShift: this.verticalShift, horizontalShift: this.horizontalShift });
  }

  /** Direct input for a shift value (value is in display units, convert to points) */
  onShiftInput(axis: 'vertical' | 'horizontal', value: string): void {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      const points = num * this.unitMultiplier;
      if (axis === 'vertical') {
        this.verticalShift = points;
      } else {
        this.horizontalShift = points;
      }
      this.shiftChanged.emit({ verticalShift: this.verticalShift, horizontalShift: this.horizontalShift });
    }
  }

  /** Direct input for scale */
  onScaleInput(value: string): void {
    const num = parseFloat(value);
    if (!isNaN(num) && num > 0) {
      this.scale = num;
      this.scaleChanged.emit(this.scale);
      this.refreshImagePosition();
    }
  }

  /** Cycle toolbar position (legacy) */
  moveToolbar(): void {
    const idx = this.toolbarPositions.indexOf(this.currentToolbarPosition);
    this.currentToolbarPosition = this.toolbarPositions[(idx + 1) % this.toolbarPositions.length];
    this.toolbarPrefs.setCoverToolbarPosition(this.currentToolbarPosition);
  }

  private coverToolbarEl: ElementRef;
  @ViewChild('coverToolbar') set coverToolbarRef(ref: ElementRef) {
    this.coverToolbarEl = ref;
    if (ref && ref.nativeElement && this.currentToolbarPosition === 'custom' && this.coverDragPosition) {
      const el = ref.nativeElement;
      el.style.top = this.coverDragPosition.y + 'px';
      el.style.left = this.coverDragPosition.x + 'px';
      el.style.bottom = 'auto';
      el.style.right = 'auto';
      el.style.transform = 'none';
    }
  }

  /** Native drag implementation for floating toolbar */
  private dragOffset = { x: 0, y: 0 };
  private boundDragMove: (e: MouseEvent | TouchEvent) => void;
  private boundDragEnd: (e: MouseEvent | TouchEvent) => void;

  onDragStart(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    const el = this.coverToolbarEl?.nativeElement;
    if (!el) { return; }
    const rect = el.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    this.dragOffset = { x: clientX - rect.left, y: clientY - rect.top };

    this.boundDragMove = (e: MouseEvent | TouchEvent) => this.onDragMove(e);
    this.boundDragEnd = () => this.onDragEnd();
    document.addEventListener('mousemove', this.boundDragMove);
    document.addEventListener('mouseup', this.boundDragEnd);
    document.addEventListener('touchmove', this.boundDragMove, { passive: false });
    document.addEventListener('touchend', this.boundDragEnd);
  }

  private onDragMove(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    const el = this.coverToolbarEl?.nativeElement;
    if (!el) { return; }
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

    el.style.top = (clientY - this.dragOffset.y) + 'px';
    el.style.left = (clientX - this.dragOffset.x) + 'px';
    el.style.bottom = 'auto';
    el.style.right = 'auto';
    el.style.transform = 'none';
  }

  private onDragEnd(): void {
    document.removeEventListener('mousemove', this.boundDragMove);
    document.removeEventListener('mouseup', this.boundDragEnd);
    document.removeEventListener('touchmove', this.boundDragMove);
    document.removeEventListener('touchend', this.boundDragEnd);
    const el = this.coverToolbarEl?.nativeElement;
    if (el) {
      this.toolbarPrefs.setCoverToolbarXY({
        x: parseInt(el.style.left, 10) || 0,
        y: parseInt(el.style.top, 10) || 0
      });
      this.currentToolbarPosition = 'custom';
      this.toolbarPrefs.setCoverToolbarPosition('custom');
    }
  }

  /** Toggle toolbar expansion for mobile */
  toggleToolbarExpanded(): void {
    this.toolbarExpanded = !this.toolbarExpanded;
    this.toolbarPrefs.setCoverToolbarExpanded(this.toolbarExpanded);
  }

  /**
   * The translated label for this editor's currently selected ruler
   * unit. Resolved through `MeasurementUnits` so `in` / `mm` is a real
   * translation row rather than a literal baked into the template --
   * see `docs/i18n/untranslatable.md`.
   */
  getUnitLabel(): string {
    return MeasurementUnits.lengthLabelFor(this.unit);
  }

  /** Get the display label for a trim size preset based on current units */
  getTrimSizeLabel(preset: any): string {
    const widthMm = Math.round(preset.width * 25.4);
    const heightMm = Math.round(preset.height * 25.4);

    if (this.unit === 'mm') {
      return `${this.localizeNumber(widthMm)} × ${this.localizeNumber(heightMm)} ${this.getUnitLabel()}`;
    } else {
      return `${this.localizeNumber(preset.width)} × ${this.localizeNumber(preset.height)} ${this.getUnitLabel()}`;
    }
  }

  /** Transform digits to locale-specific characters (e.g., Arabic-Indic numerals) */
  localizeNumber(value: any): string {
    return LanguagePipe.localizeNumber(value);
  }

  /**
   * "8.5 × 11 in" for the committed custom trim size.
   *
   * Reads `customWidth`/`customHeight` (the applied values), NOT
   * `editCustomWidth`/`editCustomHeight` (the in-progress edit
   * buffer) -- the closed `mat-option` must show what is actually in
   * effect.
   */
  getCustomTrimSizeLabel(): string {
    return this.getTrimSizeLabel({ width: this.customWidth, height: this.customHeight });
  }

  /** Get custom width in current units for display */
  getCustomWidthInCurrentUnits(): number {
    if (this.unit === 'mm') {
      return Math.round(this.editCustomWidth * 25.4);
    } else {
      return parseFloat(this.editCustomWidth.toFixed(4));
    }
  }

  /** Set custom width from value in current units */
  setCustomWidthFromCurrentUnits(value: number): void {
    if (this.unit === 'mm') {
      this.editCustomWidth = value / 25.4;
    } else {
      this.editCustomWidth = value;
    }
  }

  /** Get custom height in current units for display */
  getCustomHeightInCurrentUnits(): number {
    if (this.unit === 'mm') {
      return Math.round(this.editCustomHeight * 25.4);
    } else {
      return parseFloat(this.editCustomHeight.toFixed(4));
    }
  }

  /** Set custom height from value in current units */
  setCustomHeightFromCurrentUnits(value: number): void {
    if (this.unit === 'mm') {
      this.editCustomHeight = value / 25.4;
    } else {
      this.editCustomHeight = value;
    }
  }

  /** True when the edit values differ from the saved custom values */
  get customTrimChanged(): boolean {
    return this.editCustomWidth !== this.customWidth || this.editCustomHeight !== this.customHeight;
  }

  /** Save the custom trim values and exit edit mode */
  saveCustomTrim(): void {
    if (this.editCustomWidth > 0 && this.editCustomHeight > 0) {
      this.customWidth = this.editCustomWidth;
      this.customHeight = this.editCustomHeight;
      this.trimChanged.emit({ width: this.customWidth * 72, height: this.customHeight * 72 });
      this.editingCustomTrim = false;
      // Refresh the display after emitting trim change
      setTimeout(() => {
        this.refresh();
      }, 50);
    }
  }

  /** Cancel custom trim editing and revert to saved values */
  cancelCustomTrim(): void {
    this.editingCustomTrim = false;
    this.initTrimSize();
  }

  /** Initialize trim size selection based on current draft body dimensions */
  initTrimSize(): void {
    if (!this.draft || !this.draft.draftBody) { return; }
    const widthIn = this.draft.draftBody.width / 72;
    const heightIn = this.draft.draftBody.height / 72;
    const match = this.trimSizePresets.find(p =>
      Math.abs(p.width - widthIn) < 0.01 && Math.abs(p.height - heightIn) < 0.01
    );
    if (match) {
      this.selectedTrimSize = match.label;
    } else {
      this.selectedTrimSize = 'CUSTOM';
      this.customWidth = parseFloat(widthIn.toFixed(4));
      this.customHeight = parseFloat(heightIn.toFixed(4));
    }
  }

  /** Called when trim size preset changes */
  onTrimSizePresetChange(value: string): void {
    this.selectedTrimSize = value;
    if (value === 'CUSTOM') {
      if (this.draft && this.draft.draftBody) {
        this.customWidth = parseFloat((this.draft.draftBody.width / 72).toFixed(4));
        this.customHeight = parseFloat((this.draft.draftBody.height / 72).toFixed(4));
        this.editCustomWidth = this.customWidth;
        this.editCustomHeight = this.customHeight;
        this.editingCustomTrim = true;
      }
    } else {
      const preset = this.trimSizePresets.find(p => p.label === value);
      if (preset) {
        this.customWidth = preset.width;
        this.customHeight = preset.height;
        this.editingCustomTrim = false;
        this.trimChanged.emit({ width: preset.width * 72, height: preset.height * 72 });
        // Refresh the display after emitting trim change
        setTimeout(() => {
          this.refresh();
        }, 50);
      }
    }
  }

  /** Called when custom trim dimensions change */
  onCustomTrimSizeChange(): void {
    if (this.customWidth > 0 && this.customHeight > 0) {
      this.trimChanged.emit({ width: this.customWidth * 72, height: this.customHeight * 72 });
    }
  }

  /** Switch between inches and mm */
  changeUnits(newUnit: 'in' | 'mm'): void {
    this.unit = newUnit;
    this.unitMultiplier = newUnit === 'in' ? 72 : (72 / 25.4);
  }

  /** Round a number to the nearest hundredth for display */
  roundHundredths(value: number): number {
    return Math.round(value * 100) / 100;
  }

  /** Handle zoom slider change */
  onZoomChange(event: any): void {
    this.zoomLevel = parseFloat(event.target.value);
    // Convert zoom to displayScale: zoom 1.0 = 288 DPI (4.0 display scale)
    // This matches the interior's 288 DPI baseline
    this.displayScale = this.zoomLevel * 4.0;
    // Persist zoom so it survives component destroy/recreate (interior ↔ cover switch)
    ResourceComponent.persistedZoomLevel = this.zoomLevel;
    // Use cached image for immediate re-render
    if (this.cachedImageElement && this.cachedImageElement.complete && this.cachedImageElement.naturalWidth > 0) {
      this.refreshImagePositionDetails(this.cachedImageElement);
    } else {
      this.refreshImagePosition();
    }
  }
}
